import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getTable } from '../../api/api';
import { formatDate, truncateText } from '../../utils/helpers';

const PublicationCard = ({ pub }) => (
  <Link to={`/publication/${pub.slug}`} className="card">
    <div className="card-media">
      <img src={pub.image_url} alt={pub.title} loading="lazy" />
    </div>
    <div className="card-body">
      <div className="card-tags">
        <span className="tag">{pub.doc_type}</span>
        <span className={`tag access-${pub.access_level}`}>{pub.access_level}</span>
      </div>
      <h3>{pub.title}</h3>
      <p className="excerpt">{truncateText(pub.summary || '', 110)}</p>
      <div className="card-meta">
        <span>{pub.country_focus || ''}</span>
        <span>{formatDate(pub.publish_date)}</span>
      </div>
    </div>
  </Link>
);

const PublicationsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [publications, setPublications] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    search: '',
    category: searchParams.get('category') || '',
    type: '',
    access: searchParams.get('access') || '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getTable('tables/publications', { limit: 200 });
        setPublications(res.data || []);
        setFiltered(res.data || []);
      } catch (e) {
        console.error('Erreur chargement publications:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const { search, category, type, access } = filters;
    const q = search.toLowerCase().trim();

    let result = publications.filter((p) => {
      const matchSearch =
        !q ||
        (p.title || '').toLowerCase().includes(q) ||
        (p.summary || '').toLowerCase().includes(q) ||
        (p.country_focus || '').toLowerCase().includes(q) ||
        (p.author || '').toLowerCase().includes(q);

      const matchCategory = !category || p.category === category;
      const matchType = !type || p.doc_type === type;
      const matchAccess = !access || p.access_level === access;

      return matchSearch && matchCategory && matchType && matchAccess;
    });

    result.sort((a, b) => new Date(b.publish_date) - new Date(a.publish_date));
    setFiltered(result);
  }, [filters, publications]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({ search: '', category: '', type: '', access: '' });
    setSearchParams({});
  };

  if (loading) {
    return (
      <section className="section">
        <div className="container">
          <div className="page-header">
            <h1>Bibliothèque de publications</h1>
          </div>
          <div className="grid grid-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="card" style={{ opacity: 0.6 }}>
                <div className="card-media" style={{ background: '#e1e5eb' }} />
                <div className="card-body">
                  <h3>Chargement...</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Accueil</Link> / Publications
          </div>
          <h1>Bibliothèque de publications</h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', maxWidth: '640px' }}>
            Rapports, études, notes d'analyse et articles scientifiques sur les grands enjeux
            économiques du continent africain.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="filters-bar">
            <div className="form-group">
              <label htmlFor="searchInput">Recherche</label>
              <input
                type="text"
                id="searchInput"
                placeholder="Titre, mot-clé, pays..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="categoryFilter">Thématique</label>
              <select
                id="categoryFilter"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">Toutes</option>
                <option>Intelligence Économique</option>
                <option>Politique & Gouvernance</option>
                <option>Finance & Investissement</option>
                <option>Commerce & Industrie</option>
                <option>Énergie & Ressources</option>
                <option>Technologie & Innovation</option>
                <option>Agriculture & Développement rural</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="typeFilter">Type de document</label>
              <select
                id="typeFilter"
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <option value="">Tous</option>
                <option>Rapport</option>
                <option>Étude</option>
                <option>Article scientifique</option>
                <option>Note d'analyse</option>
                <option>Policy Brief</option>
                <option>Working Paper</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="accessFilter">Niveau d'accès</label>
              <select
                id="accessFilter"
                value={filters.access}
                onChange={(e) => handleFilterChange('access', e.target.value)}
              >
                <option value="">Tous</option>
                <option value="gratuit">Gratuit</option>
                <option value="classique">Classique</option>
                <option value="premium">Premium</option>
              </select>
            </div>

            <button className="btn btn-dark" onClick={handleResetFilters}>
              Réinitialiser
            </button>
          </div>

          <p className="results-info">
            {filtered.length} publication(s) trouvée(s)
          </p>

          {filtered.length === 0 ? (
            <div className="empty-state">
              <i className="fa-solid fa-folder-open"></i>
              Aucune publication ne correspond à votre recherche.
            </div>
          ) : (
            <div className="grid grid-3">
              {filtered.map((pub) => (
                <PublicationCard key={pub.id} pub={pub} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default PublicationsList;